import { useState, useRef } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const galleryImages = [
  {
    src: 'https://public.readdy.ai/ai/img_res/e5f0af17a04cd05948422d21c4fc68ef.jpg',
    label: 'Luxury Sedan — Automotive Tint',
    tag: 'Automotive',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=modern%20home%20large%20glass%20windows%20with%20residential%20window%20tinting%20applied%20exterior%20view%20beautiful%20house%20warm%20sunlight%20minimal%20aesthetic%20tinted%20glass%20professional%20installation%20Palm%20Beach%20Florida&width=800&height=600&seq=gal002&orientation=landscape',
    label: 'Residential Home — Window Film',
    tag: 'Residential',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=PPF%20paint%20protection%20film%20being%20applied%20on%20white%20luxury%20sports%20car%20hood%20close-up%20clear%20film%20installation%20professional%20automotive%20protection%20service&width=800&height=600&seq=gal003&orientation=landscape',
    label: 'PPF Installation — Sports Car',
    tag: 'PPF',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=luxury%20SUV%20with%20professional%20dark%20window%20tint%20applied%20side%20view%20sleek%20black%20SUV%20tinted%20windows%20all%20around%20daylight%20modern%20vehicle%20automotive%20tinting%20finished%20result&width=800&height=600&seq=gal004&orientation=landscape',
    label: 'Luxury SUV — Full Tint',
    tag: 'Automotive',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=yacht%20boat%20with%20professional%20marine%20window%20tinting%20ocean%20sunset%20background%20luxury%20yacht%20tinted%20glass%20windows%20on%20water%20South%20Florida&width=800&height=600&seq=gal005&orientation=landscape',
    label: 'Marine Yacht — Window Tint',
    tag: 'Marine',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=ceramic%20coating%20applied%20on%20red%20luxury%20sports%20car%20ultra%20glossy%20reflection%20showroom%20quality%20mirror%20shine%20automotive%20detailing%20complete%20result%20professional&width=800&height=600&seq=gal006&orientation=landscape',
    label: 'Ceramic Coating — Sports Car',
    tag: 'Ceramic',
  },
];

const beforeAfterPairs = [
  {
    before: 'https://readdy.ai/api/search-image?query=car%20window%20without%20tint%20clear%20glass%20interior%20visible%20bright%20sunlight%20glare%20hot%20interior%20stock%20vehicle%20no%20tint%20before%20treatment&width=700&height=500&seq=before001&orientation=landscape',
    after: 'https://readdy.ai/api/search-image?query=car%20window%20with%20professional%20dark%20window%20tinting%20applied%20sleek%20black%20tint%20no%20glare%20cool%20interior%20privacy%20enhanced%20after%20treatment%20complete%20result&width=700&height=500&seq=after001&orientation=landscape',
    label: 'Automotive Window Tint',
    detail: 'Same vehicle — before &amp; after professional installation',
  },
  {
    before: 'https://readdy.ai/api/search-image?query=bright%20home%20interior%20large%20windows%20without%20tint%20excessive%20glare%20hot%20sunlight%20beating%20through%20glass%20faded%20furniture%20before%20window%20film&width=700&height=500&seq=before002&orientation=landscape',
    after: 'https://readdy.ai/api/search-image?query=home%20interior%20large%20windows%20with%20window%20tinting%20comfortable%20natural%20light%20no%20glare%20cool%20interior%20beautiful%20modern%20living%20room%20after%20window%20film%20residential%20tint&width=700&height=500&seq=after002&orientation=landscape',
    label: 'Residential Window Film',
    detail: 'Same room — before &amp; after energy-efficient film',
  },
];

function BeforeAfterCard({ pair }: { pair: typeof beforeAfterPairs[0] }) {
  const [sliderPos, setSliderPos] = useState(50);
  const containerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);

  const updatePosition = (clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const pos = Math.max(5, Math.min(95, ((clientX - rect.left) / rect.width) * 100));
    setSliderPos(pos);
  };

  return (
    <div className="rounded-2xl overflow-hidden border border-white/8">
      <div
        ref={containerRef}
        className="relative h-72 sm:h-80 select-none cursor-ew-resize overflow-hidden"
        onMouseMove={(e) => { if (isDragging.current) updatePosition(e.clientX); }}
        onMouseDown={(e) => { isDragging.current = true; updatePosition(e.clientX); }}
        onMouseUp={() => { isDragging.current = false; }}
        onMouseLeave={() => { isDragging.current = false; }}
        onTouchMove={(e) => updatePosition(e.touches[0].clientX)}
      >
        {/* After (full) */}
        <img src={pair.after} alt="After" className="absolute inset-0 w-full h-full object-cover" />
        {/* Before (clipped) */}
        <div className="absolute inset-0 overflow-hidden" style={{ width: `${sliderPos}%` }}>
          <img src={pair.before} alt="Before" className="absolute inset-0 w-full h-full object-cover" style={{ width: `${10000 / sliderPos}%`, maxWidth: 'none' }} />
        </div>
        {/* Divider */}
        <div className="absolute top-0 bottom-0 w-0.5 bg-white" style={{ left: `${sliderPos}%` }}>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white flex items-center justify-center">
            <i className="ri-arrows-left-right-line text-[#0A0A0A] text-base" />
          </div>
        </div>
        {/* Labels */}
        <div className="absolute top-4 left-4 bg-black/60 text-white text-xs font-bold px-3 py-1.5 rounded-full">BEFORE</div>
        <div className="absolute top-4 right-4 bg-[#FF6B35] text-white text-xs font-bold px-3 py-1.5 rounded-full">AFTER</div>
      </div>
      <div className="bg-[#111111] px-6 py-4">
        <div className="text-white text-sm font-bold" style={{ fontFamily: 'Sora, sans-serif' }}>{pair.label}</div>
        <p className="text-white/40 text-xs mt-0.5" dangerouslySetInnerHTML={{ __html: pair.detail }} />
      </div>
    </div>
  );
}

export function Gallery() {
  const [filter, setFilter] = useState('All');
  const { ref, isVisible } = useScrollAnimation();
  const filters = ['All', 'Automotive', 'Residential', 'Marine', 'PPF', 'Ceramic'];

  const filtered = filter === 'All' ? galleryImages : galleryImages.filter((g) => g.tag === filter);

  return (
    <section id="gallery" className="py-28 bg-[#F7F5F2] relative">
      <div className="max-w-7xl mx-auto px-6 lg:px-12" ref={ref}>
        {/* Header */}
        <div className={`anim-hidden ${isVisible ? 'animate-fade-in-up' : ''} text-center mb-16`}>
          <span className="luxury-divider mx-auto" />
          <span className="text-xs font-bold uppercase tracking-[5px] text-[#C9A96E] mb-4 block">Results</span>
          <h2
            className="text-4xl sm:text-5xl font-bold text-[#0A0A0A] leading-tight tracking-tight"
            style={{ fontFamily: 'Sora, sans-serif' }}
          >
            Before &amp; After
            <br />
            <span className="text-[#FF6B35]">Transformation Gallery</span>
          </h2>
        </div>

        {/* Before/After Sliders */}
        <div className={`anim-hidden ${isVisible ? 'animate-fade-in-up delay-100' : ''} grid grid-cols-1 md:grid-cols-2 gap-6 mb-20`}>
          {beforeAfterPairs.map((pair, i) => (
            <BeforeAfterCard key={i} pair={pair} />
          ))}
        </div>

        {/* Portfolio */}
        <div className={`anim-hidden ${isVisible ? 'animate-fade-in-up delay-200' : ''} mb-8 flex flex-wrap justify-center gap-2`}>
          {filters.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                filter === f
                  ? 'bg-[#0A0A0A] text-white'
                  : 'bg-white text-gray-500 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((img, i) => (
            <div
              key={i}
              className={`anim-hidden ${isVisible ? `animate-scale-in delay-${(i % 3) * 100}` : ''} group rounded-2xl overflow-hidden bg-white border border-gray-100 cursor-default`}
            >
              <div className="relative h-56 overflow-hidden">
                <img
                  src={img.src}
                  alt={img.label}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute top-3 left-3">
                  <span className="bg-[#FF6B35] text-white text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full">
                    {img.tag}
                  </span>
                </div>
              </div>
              <div className="px-5 py-4">
                <p className="text-sm font-semibold text-[#0A0A0A]" style={{ fontFamily: 'Sora, sans-serif' }}>
                  {img.label}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
